import type {
    LocaleCode,
    OperationActivityLine,
    OperationActivityRow,
    OperationCatalogRow,
} from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { formatGel } from '@/utils/money';

/**
 * The Activity tab's pure helpers and presentational config.
 *
 * Everything here is a function of its arguments. The hook holds the filters and
 * the open drawer in `useState`; the View renders what comes out. The two things
 * worth having outside a component are the reversal mirror — which must be
 * *presentation*, not arithmetic — and where a coded refusal belongs on screen.
 */

/** Rows per page. Twenty is the journal's, and this list is read the same way. */
export const ACTIVITY_PAGE_SIZE = 20;

/** The status filter's options. `null` is "any". */
export type ActivityStatusFilter = 'ALL' | 'POSTED' | 'REVERSED';

export type ActivityFilterState = {
    from: string;
    to: string;
    /** A template key, or `'ALL'`. */
    key: string;
    status: ActivityStatusFilter;
    page: number;
};

export const initialFilters = (): ActivityFilterState => ({
    from: '',
    to: '',
    key: 'ALL',
    status: 'ALL',
    page: 1,
});

/**
 * The filter state as the request takes it.
 *
 * `'ALL'` becomes an **omitted** field, never an empty string: the server reads
 * a blank key as a filter on the empty string and answers with nothing.
 */
export const toActivityFilters = (state: ActivityFilterState) => ({
    ...(state.from ? { from: state.from } : {}),
    ...(state.to ? { to: state.to } : {}),
    ...(state.key !== 'ALL' ? { key: state.key } : {}),
    ...(state.status !== 'ALL' ? { status: state.status } : {}),
    page: state.page,
    pageSize: ACTIVITY_PAGE_SIZE,
});

/**
 * The operations the filter's select offers: the tenant's templates, by key.
 *
 * Templates only — a system row cannot be recorded, so filtering activity by one
 * would offer a choice that always answers empty. Named in the reader's own
 * language and ordered by it, which is the catalog's order re-sorted on purpose:
 * a filter is a lookup, and seed order is no help when you are looking for a
 * word.
 */
export const operationFilterOptions = (
    rows: readonly OperationCatalogRow[],
    locale: LocaleCode,
): { key: string; label: string }[] =>
    rows
        .filter((row) => row.kind === 'TEMPLATE')
        .map((row) => ({
            key: row.key,
            label: locale === 'ka' ? row.nameKa : row.nameEn,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

// ── rows, for rendering ─────────────────────────────────────────────────────

/** One row of the register, with every figure already a string. */
export type ActivityRowModel = {
    id: string;
    nameKa: string;
    nameEn: string;
    /** `v2` — the template version this posted under. */
    versionLabel: string;
    amountText: string;
    actorText: string;
    dateText: string;
    /** `2026/42`, or an en dash when the entry carries no number. */
    entryNumberText: string;
    status: OperationActivityRow['status'];
    referenceText: string;
};

/** An unknowable value renders as an en dash, never as a blank or a zero. */
const NIL = '—';

export const toRowModels = (
    rows: readonly OperationActivityRow[],
): ActivityRowModel[] =>
    rows.map((row) => ({
        id: row.recordId,
        nameKa: row.nameKa,
        nameEn: row.nameEn,
        versionLabel: `v${row.version}`,
        amountText: formatGel(row.amountTetri),
        // The id is not shown when the name is gone: a uuid in a "recorded by"
        // column is noise to every reader who could act on it.
        actorText: row.recordedByName ?? NIL,
        dateText: isoToDisplayDate(row.date),
        entryNumberText: row.entryNumberFormatted ?? NIL,
        status: row.status,
        referenceText: row.documentRef ?? NIL,
    }));

/**
 * Status chip config, keyed by the entry's status.
 *
 * Reversed is **muted, never danger** — the chart-of-accounts rule that the
 * Templates tab's Blocked chip already follows. A reversal is a correction
 * somebody made deliberately and explained in writing; colouring it as a fault
 * would make every routine correction in the register read as an incident.
 */
export const ACTIVITY_STATUS_CHIP: Record<
    OperationActivityRow['status'],
    { className: string; labelKey: string }
> = {
    POSTED: {
        className: 'bg-success-soft text-success-soft-foreground',
        labelKey: 'operations.activity.status.posted',
    },
    REVERSED: {
        className: 'bg-surface-secondary text-muted',
        labelKey: 'operations.activity.status.reversed',
    },
};

// ── the reversal mirror ─────────────────────────────────────────────────────

/**
 * The entry a reversal would post: the same lines with the columns swapped.
 *
 * **This is presentation, not arithmetic.** No amount is computed, derived or
 * rounded — each line's debit becomes its credit and back, which is exactly what
 * the server's reversal does. Recomputing the entry client-side would introduce
 * a second opinion about figures that are already final, and the one thing a
 * confirm dialog must not do is show something other than what will happen.
 *
 * The order is kept rather than re-sorted: the reader is checking a mirror, and
 * a mirror that also rearranges its subject is harder to check, not easier.
 */
export const mirrorLines = (
    lines: readonly OperationActivityLine[],
): OperationActivityLine[] =>
    lines.map((line) => ({
        ...line,
        debitTetri: line.creditTetri,
        creditTetri: line.debitTetri,
    }));

// ── server refusals ─────────────────────────────────────────────────────────

/**
 * Where a coded refusal from the reverse call belongs.
 *
 * `reason` is the only field the dialog has, so it is the only anchor besides
 * the dialog itself.
 */
export type ReverseErrorTarget = 'reason' | 'dialog';

const REVERSE_TARGETS: Record<string, ReverseErrorTarget> = {
    REASON_REQUIRED: 'reason',
};

export type ReverseError = {
    target: ReverseErrorTarget;
    message: string;
    code: string | null;
};

/**
 * Turn a failed reversal into something to render.
 *
 * The server's prose, verbatim — the same trade the editor and the Record tab
 * document, and for the same reason: these messages name the period, the entry
 * or the rule, and a translated string could not carry those without the server
 * sending each part separately.
 *
 * **`ENTRY_REVERSED` is not pre-hidden.** A second Reverse attempt is refused by
 * the server, and the dialog surfaces that refusal rather than the client
 * guessing which entries are still reversible — the drawer's own state can be a
 * moment behind another admin's.
 */
export const toReverseError = (
    error: ErrorResponse | null | undefined,
): ReverseError | null => {
    if (!error) {
        return null;
    }
    const code = error.code ?? null;
    return {
        target: (code && REVERSE_TARGETS[code]) || 'dialog',
        message: error.message,
        code,
    };
};
